/**
 * Rechnungsliste.
 *
 * Überfällige Rechnungen stehen oben hervorgehoben — sie sind der einzige
 * Grund, diese Liste regelmäßig zu öffnen.
 */
import { useMemo, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Seite } from '../bausteine/Layout';
import { Marker } from '../bausteine/Marker';
import { Fehlermeldung } from '../bausteine/Meldung';
import { Laedt, Leer } from '../bausteine/Zustand';
import { useAbruf } from '../lib/abruf';
import { alsAbfrage } from '../lib/api';
import { datum, jahrHeute } from '../lib/format';
import { alsCent, euro, formatiereCent } from '../lib/geld';
import type { RechnungListe } from '../lib/typen';

export function Rechnungen(): ReactNode {
  const [filter, setzeFilter] = useSearchParams();
  const lese = (name: string): string => filter.get(name) ?? '';

  function aendere(name: string, wert: string): void {
    const neu = new URLSearchParams(filter);
    if (wert === '') neu.delete(name);
    else neu.set(name, wert);
    setzeFilter(neu, { replace: true });
  }

  const pfad = useMemo(
    () =>
      `/api/rechnungen${alsAbfrage({
        status: lese('status'),
        offen: lese('offen'),
        jahr: lese('jahr'),
        proSeite: 100,
      })}`,
    [filter.toString()],
  );

  const liste = useAbruf<RechnungListe>(pfad);

  const offeneSumme = (liste.daten?.rechnungen ?? [])
    .filter((r) => r.status === 'gestellt' || r.status === 'teilbezahlt')
    .reduce((s, r) => s + alsCent(r.betragBrutto), 0);

  return (
    <Seite
      titel="Rechnungen"
      untertitel={
        liste.daten
          ? offeneSumme > 0
            ? `${formatiereCent(offeneSumme)} offen`
            : `${liste.daten.gesamt} insgesamt`
          : undefined
      }
      aktionen={
        <Link to="/rechnungen/neu" className="knopf haupt">
          Rechnung schreiben
        </Link>
      }
    >
      <div className="karte">
        <div className="filterleiste">
          <div className="feld">
            <label htmlFor="status">Status</label>
            <select id="status" value={lese('status')} onChange={(e) => aendere('status', e.target.value)}>
              <option value="">alle</option>
              <option value="entwurf">Entwurf</option>
              <option value="gestellt">Gestellt</option>
              <option value="teilbezahlt">Teilbezahlt</option>
              <option value="bezahlt">Bezahlt</option>
              <option value="storniert">Storniert</option>
            </select>
          </div>

          <div className="feld">
            <label htmlFor="jahr">Jahr</label>
            <select id="jahr" value={lese('jahr')} onChange={(e) => aendere('jahr', e.target.value)}>
              <option value="">alle</option>
              {[jahrHeute(), jahrHeute() - 1, jahrHeute() - 2].map((j) => (
                <option key={j} value={j}>
                  {j}
                </option>
              ))}
            </select>
          </div>

          <div className="feld">
            <label htmlFor="offen">Zahlung</label>
            <select id="offen" value={lese('offen')} onChange={(e) => aendere('offen', e.target.value)}>
              <option value="">alle</option>
              <option value="true">nur unbezahlt</option>
            </select>
          </div>
        </div>

        {liste.fehler ? (
          <div className="karte-koerper">
            <Fehlermeldung text={liste.fehler} />
          </div>
        ) : !liste.daten ? (
          <Laedt was="Rechnungen werden geladen" />
        ) : liste.daten.rechnungen.length === 0 ? (
          <Leer
            titel="Keine Rechnung"
            hinweis="Eine Rechnung entsteht als Entwurf und bekommt ihre Nummer erst beim Stellen."
          />
        ) : (
          <div className="tabelle-rahmen">
            <table className="tabelle">
              <thead>
                <tr>
                  <th className="schmal">Nummer</th>
                  <th className="schmal">Datum</th>
                  <th>Empfänger</th>
                  <th className="schmal">Fällig</th>
                  <th className="zahl">Betrag</th>
                  <th className="schmal">Status</th>
                </tr>
              </thead>
              <tbody>
                {liste.daten.rechnungen.map((r) => (
                  <tr key={r.id}>
                    <td className="schmal fest">
                      <Link to={`/rechnungen/${r.id}`}>{r.nummer ?? 'Entwurf'}</Link>
                    </td>
                    <td className="schmal">{datum(r.rechnungsdatum)}</td>
                    <td>
                      <span className="haupttext">{r.empfaenger ?? '—'}</span>
                      {r.betreff ? <div className="nebentext">{r.betreff}</div> : null}
                    </td>
                    <td className="schmal">{datum(r.faelligAm)}</td>
                    <td className="zahl">{euro(r.betragBrutto, false)}</td>
                    <td className="schmal">
                      {/* Überfällig ist kein eigener Status in der
                          Datenbank, sondern ergibt sich aus Fälligkeit und
                          Zahlung. Angezeigt wird es trotzdem als solcher —
                          für den Benutzer ist es einer. */}
                      <Marker status={r.ueberfaellig ? 'ueberfaellig' : r.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Seite>
  );
}
